"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import { useState } from "react";
import SlideBackground from "./SlideBackground";
import {
  TRUSTPILOT_FIVE_STAR_COUNT,
  TRUSTPILOT_PROFILE_URL,
} from "@/constants/Trustpilot";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

interface SlideData {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  backgroundImage: string;
  stats: {
    label: string;
    value: string;
  }[];
  cta: {
    primary: string;
    secondary: string;
  };
  badge: string;
}

const slides: SlideData[] = [
  {
    id: 2,
    title: "Science-backed.",
    subtitle: "Doctor-led.",
    description:
      "Medical experts combining cutting-edge treatments with personalised nutrition plans.",
    backgroundImage: "/images/hero/4.png",
    stats: [
      { label: "Experts", value: "200+" },
      { label: "MHRA", value: "100%" },
      { label: "Rating", value: "4.9★" },
    ],
    cta: {
      primary: "Book Consultation",
      secondary: "Learn More",
    },
    badge: "MHRA Approved",
  },
  {
    id: 5,
    title: "Lose weight.",
    subtitle: "Keep it off.",
    description:
      "Join 50,000+ people transforming their lives with medically-supervised weight loss programs.",
    backgroundImage:
      "https://clinic-digital.lon1.cdn.digitaloceanspaces.com/uk-weight-loss/hero/AdobeStock_227897902.jpeg",
    stats: [
      { label: "Experts", value: "200+" },
      { label: "MHRA", value: "100%" },
      { label: "Rating", value: "4.9★" },
    ],
    cta: {
      primary: "Book Consultation",
      secondary: "Learn More",
    },
    badge: "Medically Supervised",
  },
  {
    id: 6,
    title: "Personalised care.",
    subtitle: "Real results.",
    description:
      "Tailored treatment plans designed by medical professionals for your unique weight loss journey.",
    backgroundImage:
      "https://clinic-digital.lon1.cdn.digitaloceanspaces.com/uk-weight-loss/hero/AdobeStock_230583986.jpeg",
    stats: [
      { label: "Experts", value: "200+" },
      { label: "MHRA", value: "100%" },
      { label: "Rating", value: "4.9★" },
    ],
    cta: {
      primary: "Book Consultation",
      secondary: "Learn More",
    },
    badge: "Personalised Plans",
  },
  {
    id: 7,
    title: "Expert guidance.",
    subtitle: "Every step.",
    description:
      "Comprehensive support from certified medical professionals throughout your transformation.",
    backgroundImage:
      "https://clinic-digital.lon1.cdn.digitaloceanspaces.com/uk-weight-loss/hero/AdobeStock_298320945.jpeg",
    stats: [
      { label: "Experts", value: "200+" },
      { label: "MHRA", value: "100%" },
      { label: "Rating", value: "4.9★" },
    ],
    cta: {
      primary: "Book Consultation",
      secondary: "Learn More",
    },
    badge: "Expert Support",
  },
  {
    id: 3,
    title: "Transform fast.",
    subtitle: "Stay strong.",
    description:
      "Revolutionary treatments that work 3x faster with ongoing support from day one.",
    backgroundImage: "/images/hero/3.png",
    stats: [
      { label: "Faster", value: "3x" },
      { label: "Success", value: "89%" },
      { label: "Support", value: "24/7" },
    ],
    cta: {
      primary: "Get Started Today",
      secondary: "View Programs",
    },
    badge: "3x Faster Results",
  },
  {
    id: 1,
    title: "Clinically proven.",
    subtitle: "Life-changing.",
    description:
      "MHRA-approved treatments delivering sustainable weight loss with continuous medical oversight.",
    backgroundImage:
      "https://clinic-digital.lon1.cdn.digitaloceanspaces.com/uk-weight-loss/hero/AdobeStock_194774555.jpeg",
    stats: [
      { label: "Experts", value: "200+" },
      { label: "MHRA", value: "100%" },
      { label: "Rating", value: "4.9★" },
    ],
    cta: {
      primary: "Book Consultation",
      secondary: "Learn More",
    },
    badge: "Clinically Proven",
  },
];

export default function MobileSlideShow() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="relative p-3 sm:p-4 bg-white md:hidden">
      <div className="relative h-[calc(100vh-150px)] min-h-[600px] overflow-hidden rounded-[24px] shadow-2xl">
        <Swiper
          modules={[Navigation, Pagination, Autoplay, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          autoplay={{
            delay: 8000,
            disableOnInteraction: false,
          }}
          speed={1200}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          className="h-full w-full rounded-[24px]"
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className="relative h-full w-full rounded-[24px] overflow-hidden">
                {/* Full coverage background */}
                <div className="absolute inset-0">
                  <div className="absolute inset-0">
                    <SlideBackground
                      imageSrc={slide.backgroundImage}
                      fallbackText={`Background: ${slide.backgroundImage}`}
                      alt={`${slide.title} ${slide.subtitle} background`}
                    />
                  </div>
                  {/* Dark image overlay for reduced opacity */}
                  <div className="absolute inset-0 bg-gray-900/40 z-[5]"></div>
                </div>

                {/* Dark gradient overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent z-10 pointer-events-none"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-transparent to-transparent z-10 pointer-events-none"></div>

                {/* Content Container */}
                <div className="relative z-30 h-full flex flex-col justify-center pt-24 pb-16">
                  <div className="px-6 space-y-5">
                    {/* Trustpilot five-star count, no overall score: see constants/Trustpilot.ts */}
                    <div className="flex justify-center mb-2">
                      <a
                        href={TRUSTPILOT_PROFILE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center bg-white/95 backdrop-blur-md rounded-full px-4 py-2.5 shadow-lg border border-white/50"
                        aria-label={`${TRUSTPILOT_FIVE_STAR_COUNT}+ five-star reviews on Trustpilot (opens in a new tab)`}
                      >
                        <svg
                          className="w-4 h-4 text-[#00B67A] mr-1.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-sm font-bold text-gray-900">
                          Trustpilot
                        </span>
                        <span className="text-xs text-gray-600 ml-1.5 font-semibold">
                          {TRUSTPILOT_FIVE_STAR_COUNT}+ five-star reviews
                        </span>
                      </a>
                    </div>

                    {/* Headline */}
                    <div className="text-center space-y-2">
                      <h1 className="text-[40px] sm:text-5xl font-semibold leading-[1.1] tracking-tight font-[family-name:var(--font-playfair)]">
                        <span className="block text-white mb-1">
                          {slide.title}
                        </span>
                        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#9d97d8] via-[#b8b3e6] to-purple-300">
                          {slide.subtitle}
                        </span>
                      </h1>
                    </div>

                    {/* Description */}
                    <p className="text-lg sm:text-xl text-gray-200 leading-relaxed text-center max-w-md mx-auto font-light tracking-wide">
                      {slide.description}
                    </p>

                    {/* Action Buttons */}
                    <div className="space-y-3 pt-2">
                      <a
                        href="https://ukweightloss.co.uk/consultation/GUHMJBNGE33R7CJ"
                        className="group relative w-full bg-gradient-to-r from-[#7771B8] via-[#8f89cc] to-[#7771B8] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white px-8 py-4 rounded-xl font-semibold text-base shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] overflow-hidden block text-center"
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          {slide.cta.primary}
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-[#6860a3] to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      </a>
                      <a
                        href="https://ukweightloss.co.uk/products"
                        className="group w-full border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-xl font-semibold text-base bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-xl block text-center"
                      >
                        <span className="flex items-center justify-center gap-2">
                          {slide.cta.secondary}
                          <svg
                            className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </span>
                      </a>
                    </div>

                    {/* Navigation Dots */}
                    <div className="flex justify-center space-x-3 pt-4">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          className={`h-3 rounded-full transition-all duration-500 ${
                            index === activeIndex
                              ? "w-12 bg-gradient-to-r from-white to-gray-200 shadow-lg"
                              : "w-3 bg-white/40 hover:bg-white/60 hover:scale-110"
                          }`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
